// Chatbot API Service
// Handles all chatbot-related API calls

import { API_BASE_URL as RAW_API_BASE_URL } from './api';
import { fetchWithAuthRetry } from './refreshCoordinator';

const API_BASE_URL = RAW_API_BASE_URL.replace(/\/$/, '');

export interface ChatbotMessage {
  message: string;
  session_id: string;
  user_type: 'patient' | 'staff' | 'visitor';
  context?: Record<string, any>;
}

export interface ChatbotResponse {
  response: string;
  conversation_id: string;
  message_id: string;
  guidelines_applied: number;
  medical_safety_level: 'standard' | 'elevated' | 'critical';
  requires_escalation: boolean;
  requires_human_review: boolean;
  tools_executed: string[];
  processing_time_ms: number;
  usage?: {
    input_tokens: number;
    output_tokens: number;
  };
}

export interface UserSearchResult {
  id: number;
  username: string;
  full_name: string;
  role: string;
  department: string | null;
  unit: string | null;
  match_score: number;
  match_reasons: string[];
  can_delegate: boolean;
}

export interface UserSearchResponse {
  users: UserSearchResult[];
  total_found: number;
  search_metadata: {
    query_processed: string;
    fuzzy_matching: boolean;
    confidence: number;
  };
}

export interface MessageDelegationRequest {
  target_user_id: number;
  message_content: string;
  delegation_reason: string;
  urgency_level?: 'low' | 'medium' | 'high' | 'urgent';
  context?: Record<string, any>;
}

export interface MessageDelegationResponse {
  success: boolean;
  message_id?: number;
  delegation_id: string;
  status: string;
  requires_approval: boolean;
  ai_confidence: number;
  processing_time_ms?: number;
  message?: string;
  error?: string;
}

export interface DelegationHistoryItem {
  id: string;
  target_user: {
    id: number;
    name: string;
    role: string;
  };
  message_content: string;
  delegation_reason: string;
  urgency_level: string;
  status: string;
  requires_approval: boolean;
  ai_confidence_score: number;
  created_at: string;
  processed_at?: string;
  message_id?: number;
  approved_by?: string;
}

export interface DelegationHistoryResponse {
  requests: DelegationHistoryItem[];
  pagination: {
    page: number;
    page_size: number;
    total_count: number;
    total_pages: number;
  };
}

class ChatbotApiService {
  private getCSRFToken(): string | null {
    // Extract CSRF token from cookie for CSRF protection
    const name = 'csrftoken';
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.substring(0, name.length + 1) === (name + '=')) {
          cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
          break;
        }
      }
    }
    return cookieValue;
  }

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    // Add CSRF token for POST requests
    const csrfToken = this.getCSRFToken();
    if (csrfToken) {
      headers['X-CSRFToken'] = csrfToken;
    }
    
    return headers;
  }

  private async makeRequest(url: string, options: RequestInit = {}): Promise<Response> {
    // Ensure cookies are included in all requests; self-heals an expired
    // access cookie (e.g. after the tab was backgrounded on mobile) by
    // refreshing and retrying once instead of failing the call outright.
    const requestOptions: RequestInit = {
      ...options,
      headers: {
        ...this.getHeaders(),
        ...options.headers,
      },
    };

    return fetchWithAuthRetry(url, requestOptions);
  }

  async sendMessage(data: ChatbotMessage): Promise<ChatbotResponse> {
    
    // Use the original endpoint - the SynchronousOnlyOperation error has been fixed
    const response = await this.makeRequest(`${API_BASE_URL}/chatbot/message/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });


    if (!response.ok) {
      const errorText = await response.text();
      
      if (response.status === 401) {
        throw new Error('Authentication required. Please log in.');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  }

  async getConversationHistory(sessionId: string, limit = 50, offset = 0) {
    const response = await this.makeRequest(
      `${API_BASE_URL}/chatbot/conversation/${sessionId}/?limit=${limit}&offset=${offset}`
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async escalateConversation(sessionId: string, reason?: string) {
    const response = await this.makeRequest(
      `${API_BASE_URL}/chatbot/conversation/${sessionId}/escalate/`,
      {
        method: 'POST',
        body: JSON.stringify({ reason: reason || 'User requested human assistance' }),
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async checkHealth() {
    const response = await this.makeRequest(`${API_BASE_URL}/chatbot/health/`);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async getStats(days = 7) {
    const response = await this.makeRequest(
      `${API_BASE_URL}/chatbot/stats/?days=${days}`
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // New AI User Search and Message Delegation methods

  async searchUsers(
    query: string,
    maxResults = 10,
    filters?: {
      departments?: string[];
      roles?: string[];
      units?: string[];
    }
  ): Promise<UserSearchResponse> {
    const response = await this.makeRequest(`${API_BASE_URL}/messaging/ai/search-users/`, {
      method: 'POST',
      body: JSON.stringify({
        query,
        max_results: maxResults,
        filters: filters || {}
      }),
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication required. Please log in.');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async delegateMessage(request: MessageDelegationRequest): Promise<MessageDelegationResponse> {
    const response = await this.makeRequest(`${API_BASE_URL}/messaging/ai/delegate-message/`, {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication required. Please log in.');
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async getDelegationHistory(
    params?: {
      page?: number;
      page_size?: number;
      status?: string;
      start_date?: string;
      end_date?: string;
      requires_approval?: boolean;
    }
  ): Promise<{ delegations: any[]; pagination?: any }> {
    const searchParams = new URLSearchParams();

    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.page_size) searchParams.append('page_size', params.page_size.toString());
    if (params?.status) searchParams.append('status', params.status);
    if (params?.start_date) searchParams.append('start_date', params.start_date);
    if (params?.end_date) searchParams.append('end_date', params.end_date);
    if (params?.requires_approval !== undefined) {
      searchParams.append('requires_approval', params.requires_approval.toString());
    }

    const response = await this.makeRequest(
      `${API_BASE_URL}/messages/ai/delegation-history/?${searchParams}`
    );

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication required. Please log in.');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async approveDelegation(
    delegationId: string, 
    approval: { approved: boolean; approval_notes?: string }
  ): Promise<{ success: boolean; message_id?: number; status: string }> {
    const response = await this.makeRequest(
      `${API_BASE_URL}/messages/ai/approve-delegation/${delegationId}/`,
      {
        method: 'POST',
        body: JSON.stringify(approval),
      }
    );

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication required. Please log in.');
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Chat History methods for AI chatbot
  async getChatHistory(filters: any = {}) {
    const searchParams = new URLSearchParams();
    
    if (filters.search) searchParams.append('search', filters.search);
    if (filters.category) searchParams.append('category', filters.category);
    if (filters.archived !== undefined) searchParams.append('archived', filters.archived.toString());
    if (filters.favorite !== undefined) searchParams.append('favorite', filters.favorite.toString());
    if (filters.limit !== undefined) searchParams.append('limit', filters.limit.toString());
    if (filters.offset !== undefined) searchParams.append('offset', filters.offset.toString());

    const response = await this.makeRequest(
      `${API_BASE_URL}/chatbot/history/?${searchParams}`
    );

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication required. Please log in.');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async getChatHistoryDetail(sessionId: string, params?: { limit?: number; offset?: number }) {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.offset) searchParams.append('offset', params.offset.toString());

    const response = await this.makeRequest(
      `${API_BASE_URL}/chatbot/history/${sessionId}/?${searchParams}`
    );

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication required. Please log in.');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async updateChatHistory(sessionId: string, data: any) {
    const response = await this.makeRequest(
      `${API_BASE_URL}/chatbot/history/${sessionId}/`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication required. Please log in.');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async archiveChatHistory(sessionId: string) {
    const response = await this.makeRequest(
      `${API_BASE_URL}/chatbot/history/${sessionId}/archive/`,
      {
        method: 'POST',
      }
    );

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication required. Please log in.');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async unarchiveChatHistory(sessionId: string) {
    const response = await this.makeRequest(
      `${API_BASE_URL}/chatbot/history/${sessionId}/unarchive/`,
      {
        method: 'POST',
      }
    );

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication required. Please log in.');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async toggleChatHistoryFavorite(sessionId: string) {
    const response = await this.makeRequest(
      `${API_BASE_URL}/chatbot/history/${sessionId}/favorite/`,
      {
        method: 'POST',
      }
    );

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication required. Please log in.');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async deleteChatHistory(sessionId: string, data?: any) {
    const response = await this.makeRequest(
      `${API_BASE_URL}/chatbot/history/${sessionId}/delete/`,
      {
        method: 'DELETE',
        body: data ? JSON.stringify(data) : undefined,
      }
    );

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication required. Please log in.');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async bulkDeleteChatHistory(data: any) {
    const response = await this.makeRequest(
      `${API_BASE_URL}/chatbot/history/bulk-delete/`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication required. Please log in.');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async restoreChatHistory(sessionId: string) {
    const response = await this.makeRequest(
      `${API_BASE_URL}/chatbot/history/${sessionId}/restore/`,
      {
        method: 'POST',
      }
    );

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication required. Please log in.');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async getDeletedChatHistory(params?: { limit?: number; offset?: number }) {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.offset) searchParams.append('offset', params.offset.toString());

    const response = await this.makeRequest(
      `${API_BASE_URL}/chatbot/history/deleted/?${searchParams}`
    );

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication required. Please log in.');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }
}

export const chatbotApi = new ChatbotApiService();